import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { useSweepApi, crmOrgHeader } from '../apis/sweep/useSweepApi';
import {
  selectUserInfoData,
  selectOrgPermissions,
  setUserGetStarted,
  setUserInfo,
  updateOnboardingStep,
  setUserFetchSuccess,
  setUserFetchError,
  setTrialEndDate,
  setUserQuestionnaire,
  setUserPermissions,
  setUserMcpOrgId,
  setNoAccountSetError,
} from '../reducers/userInfoReducer';
import { setAccountUsersData } from '../reducers/accountUsersReducer';
import type { UserInfo } from '../types/UserInfoTypes';
import { Template } from '../types/UserInfoTypes';
import { useUserApiFacade } from '../apis/facades/useUserApiFacade';
import type { GetStartedActionIdExtended } from '../components/pages/get-started/getStartedTypes';
import { GetStartedActionIdDerived } from '../components/pages/get-started/getStartedTypes';
import type { GetStartedActionId } from '@server/get-started';
import { ActionStatus } from '@server/get-started';
import { DefaultUserLevelStatus } from '../components/pages/get-started/getStartedConsts';
import type { AccountOnboardingStep } from '@server/account-onboarding';
import type { UpdateUserAndAccountDetails } from '../apis/facades/useDashboardApiFacade';
import useDashboardApiFacade from '../apis/facades/useDashboardApiFacade';
import { useSweepUnauthenticatedApi } from '../apis/sweep/useSweepUnauthenticatedApi';
import extractErrorMsg from '../components/helpers/extractErrorMsg';
import type { QuestionnaireData } from '../components/pages/questionnaire/questionnaireTypes';
import { QuestionnaireStatus } from '@server/questionnaire';
import { telemetry } from '../telemetry';
import { selectConnectedCrmOrgs } from '../components/pages/environments/environmentsReducer';
import { useSweepNotifications } from '../components/notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../reducers/notificationsReducer';
import type { Permission } from '@server/permissions';

export enum SweepRoles {
  Viewer = 'Viewer',
  Collaborator = 'Collaborator',
  Admin = 'Admin',
  Alert_Manager = 'Alert Manager',
  AgentOperator = 'Agent Operator',
}

export const TRIAL_DAYS_DEFAULT = 14;

const useUserInfo = () => {
  const userInfo = useSelector(selectUserInfoData);
  const orgPermissions = useSelector(selectOrgPermissions);
  const sweepApi = useSweepApi();
  const unauthenticatedApi = useSweepUnauthenticatedApi();
  const dispatch = useDispatch();
  const {
    fetch_current_user,
    delete_user,
    delete_invitation,
    patch_updateOnboardingStatus,
    update_userPreferences,
    post_accountTrial,
    get_clearbitEnrichment,
    get_permissions,
    post_hubspotEnrichment,
    set_mcp_org_for_user,
  } = useUserApiFacade();
  const { put_accountDetails } = useDashboardApiFacade();
  const { addNotification } = useSweepNotifications();

  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await fetch_current_user();
      dispatch(setUserFetchSuccess(user));
      return user;
    } catch (error: any) {
      const msg = extractErrorMsg(error);

      if (error?.status === 403) {
        dispatch(setNoAccountSetError());
        return;
      }

      dispatch(setUserFetchError(msg));

      const { statusCode } = error?.response?.data || {};
      if (statusCode !== 401) {
        telemetry.captureError(error, { message: msg });
      }
    }
  }, [dispatch, fetch_current_user]);

  const getPermissions = useCallback(async () => {
    const permissions = await get_permissions();
    dispatch(setUserPermissions(permissions));
    return permissions;
  }, [dispatch, get_permissions]);

  const checkIfUserHasPermissionsForOrg = useCallback(
    (crmOrgId: string, permission: string, message?: string) => {
      const permissions = orgPermissions?.[crmOrgId] || [];
      const hasPermissions = permissions.includes(permission as Permission);
      if (!hasPermissions && message) {
        addNotification({
          message,
          variant: SweepNotificationVariant.Error,
        });
      }
      return hasPermissions;
    },
    [orgPermissions, addNotification],
  );

  const updateUserInfo = async (updatedUserInfo: Partial<UserInfo>) => {
    const nothingChanged = isEqual({ ...userInfo, ...updatedUserInfo }, userInfo);

    if (nothingChanged) return;
    try {
      await sweepApi.patch(`/users`, updatedUserInfo);
      dispatch(setUserInfo(updatedUserInfo));
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const updateGetStartedActionCompleted = useCallback(
    async (actionId: GetStartedActionIdExtended) => {
      const accountLevelActions = Object.values(GetStartedActionIdDerived);

      if (accountLevelActions.includes(actionId as GetStartedActionIdDerived)) {
        return;
      }

      //since we know the action is not account-level (checked by the above "if"), we can do the casting
      const _actionId = actionId as GetStartedActionId;

      const clonedGetStarted = cloneDeep(userInfo?.preferences?.getStarted);
      const updatedGetStarted = { ...DefaultUserLevelStatus, ...clonedGetStarted };
      if (updatedGetStarted?.[_actionId]) {
        updatedGetStarted[_actionId] = ActionStatus.COMPLETED;
      }
      const updatedPreferences = { ...userInfo?.preferences, getStarted: updatedGetStarted };
      try {
        await update_userPreferences(updatedPreferences);
        dispatch(setUserGetStarted(updatedGetStarted));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [dispatch, update_userPreferences, userInfo?.preferences],
  );

  const updateDefaultCreationCrmOrgId = useCallback(
    async (crmOrgId: string | null) => {
      const updatedPreferences = {
        ...userInfo?.preferences,
        defaultCreationCrmOrgId: crmOrgId,
      };
      try {
        dispatch(setUserInfo({ preferences: updatedPreferences }));
        await update_userPreferences(updatedPreferences);
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [dispatch, update_userPreferences, userInfo?.preferences],
  );

  const updateUserQuestionnaireCompleted = useCallback(
    async (questionnaireData?: Partial<QuestionnaireData>) => {
      const updatedData = { ...userInfo?.preferences?.questionnaire?.data, ...questionnaireData };
      const questionnaire = {
        data: updatedData,
        status: QuestionnaireStatus.COMPLETED,
      };
      const updatedPreferences = {
        ...userInfo?.preferences,
        questionnaire,
      };
      try {
        await update_userPreferences(updatedPreferences);
        dispatch(setUserQuestionnaire(questionnaire));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [dispatch, update_userPreferences, userInfo?.preferences],
  );

  const updateUserQuestionnaire = useCallback(
    async (questionnaireData: Partial<QuestionnaireData>) => {
      const updatedData = {
        ...userInfo?.preferences?.questionnaire?.data,
        ...questionnaireData,
      };
      const questionnaire = {
        data: updatedData,
        status: userInfo?.preferences?.questionnaire?.status ?? QuestionnaireStatus.NOT_COMPLETED,
      };
      const updatedPreferences = {
        ...userInfo?.preferences,
        questionnaire,
      };
      try {
        await update_userPreferences(updatedPreferences);
        dispatch(setUserQuestionnaire(questionnaire));
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [dispatch, update_userPreferences, userInfo?.preferences],
  );

  const addNewImage = async (imageBase64Blob: string) => {
    try {
      const response = await sweepApi.post(`/users/image`, { image: imageBase64Blob });
      return response.data as string;
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const getAccountUsersData = async () => {
    const response = await sweepApi.get(
      `/users/account?includeInvitations=true`,
      crmOrgHeader(null),
    );
    dispatch(setAccountUsersData(response.data));
    return response.data;
  };

  const updateUserRoles = async (userId: string, newRole: string) => {
    try {
      const response = await sweepApi.put(`/users/${userId}/role`, {
        roleGroupId: newRole,
      });
      return response.data as string;
    } catch (e) {
      telemetry.captureError(e);
    }
  };

  const sendInvitation = async ({
    email,
    roleGroupId,
    message,
    entryPointName,
    entryPointId,
    template = Template.Account,
  }: {
    email: string;
    roleGroupId: string;
    message?: string;
    entryPointName?: string;
    entryPointId?: string;
    template?: Template;
  }) => {
    await sweepApi.post('/users/account/invitations', {
      email,
      roleGroupId,
      message,
      entryPointName,
      entryPointId,
      template,
    });
  };

  const removeUser = async (userId: string) => {
    return await delete_user(userId);
  };

  const removeInvitation = async (email: string) => {
    return await delete_invitation(email);
  };

  const updateOnboardingStatus = async ({ status }: { status: AccountOnboardingStep }) => {
    await patch_updateOnboardingStatus(status);
    dispatch(updateOnboardingStep(status));
  };

  const updateAccountDetails = async (userAndAccountDetails: UpdateUserAndAccountDetails) => {
    await put_accountDetails(userAndAccountDetails);
    dispatch(
      setUserInfo({
        name: userAndAccountDetails.userName,
        account: { ...userInfo?.account, name: userAndAccountDetails.accountName },
      }),
    );
  };

  const resendVerificationEmail = useCallback(
    async (userId: string) => {
      await unauthenticatedApi.post(`/users/${userId}/resend-verification-email`);
    },
    [unauthenticatedApi],
  );

  const startTrial = useCallback(async () => {
    await post_accountTrial();
    const endDate = new Date(Date.now() + 1000 * 60 * 60 * 24 * TRIAL_DAYS_DEFAULT).toISOString();
    dispatch(setTrialEndDate({ trialEndDate: endDate }));
  }, [dispatch, post_accountTrial]);

  const crmOrgs = useSelector(selectConnectedCrmOrgs);

  const updateDefaultCreationCrmOrgIdIfNecessary = useCallback(
    async (crmOrgId: string) => {
      if (crmOrgs.length === 0) {
        if (crmOrgId) {
          await updateDefaultCreationCrmOrgId(crmOrgId);
        }
      }
    },
    [crmOrgs.length, updateDefaultCreationCrmOrgId],
  );

  const getClearbitEnrichment = useCallback(async () => {
    try {
      return await get_clearbitEnrichment();
    } catch (e) {
      telemetry.captureError(e);
    }
  }, [get_clearbitEnrichment]);

  const updateHubspotContact = useCallback(
    async (payload: Record<string, any>) => {
      try {
        return await post_hubspotEnrichment(payload);
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [post_hubspotEnrichment],
  );

  const handleSetUserMcpOrgId = useCallback(
    async (orgId: string) => {
      try {
        await set_mcp_org_for_user(orgId);
        dispatch(setUserMcpOrgId(orgId));
      } catch (e) {
        addNotification({
          message: 'Error setting MCP environment',
          variant: SweepNotificationVariant.Error,
        });
        telemetry.captureError(e);
      }
    },
    [set_mcp_org_for_user, dispatch, addNotification],
  );

  return {
    checkIfUserHasPermissionsForOrg,
    fetchCurrentUser,
    getPermissions,
    getAccountUsersData,
    updateUserInfo,
    addNewImage,
    sendInvitation,
    updateUserRoles,
    removeUser,
    removeInvitation,
    updateOnboardingStatus,
    updateAccountDetails,
    resendVerificationEmail,
    updateDefaultCreationCrmOrgId,
    startTrial,
    updateUserQuestionnaire,
    updateUserQuestionnaireCompleted,
    updateGetStartedActionCompleted,
    updateDefaultCreationCrmOrgIdIfNecessary,
    getClearbitEnrichment,
    updateHubspotContact,
    handleSetUserMcpOrgId,
  };
};

export default useUserInfo;
